import * as React from "react";

export interface FormRowProps {
  label: React.ReactNode;
  required?: boolean;
  help?: React.ReactNode;
  error?: string;
  /** Force a specific control id. Overrides any auto-generated or child id. */
  htmlFor?: string;
  /** Force vertical (label-on-top) layout regardless of viewport.
   *  Default `false`: uses 2-column grid at md+ for wide settings panels.
   *  Set `true` for narrow contexts like login cards or modals. */
  stacked?: boolean;
  className?: string;
  children: React.ReactElement;
}

type ChildProps = {
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
};

function joinIds(...ids: Array<string | undefined>): string | undefined {
  const parts = ids.filter(Boolean) as string[];
  return parts.length ? parts.join(" ") : undefined;
}

export function FormRow({
  label,
  required,
  help,
  error,
  htmlFor,
  stacked = false,
  className,
  children,
}: FormRowProps) {
  const autoControlId = React.useId();
  const labelId = React.useId();
  const helpId = React.useId();
  const errorId = React.useId();

  // Read children's existing props (single ReactElement contract).
  const childProps = (children.props ?? {}) as ChildProps;
  const childId = childProps.id;
  const childLabelledBy = childProps["aria-labelledby"];
  const childDescribedBy = childProps["aria-describedby"];

  // effectiveControlId priority:
  //  1. props.htmlFor (explicit override)
  //  2. child.props.id (consumer set on the control directly)
  //  3. autoControlId (generated)
  const effectiveControlId = htmlFor ?? childId ?? autoControlId;

  // aria-describedby: join help/error ids with any value the child already set.
  const describedByExtras: string[] = [];
  if (help) describedByExtras.push(helpId);
  if (error) describedByExtras.push(errorId);
  const effectiveDescribedBy = joinIds(
    childDescribedBy,
    ...describedByExtras,
  );

  // aria-labelledby: prefer child's own value if set, else point at our label.
  const effectiveLabelledBy = childLabelledBy ?? labelId;

  const cloned = React.cloneElement(children, {
    id: effectiveControlId,
    "aria-labelledby": effectiveLabelledBy,
    ...(effectiveDescribedBy
      ? { "aria-describedby": effectiveDescribedBy }
      : {}),
  } as Partial<ChildProps>);

  const rootClasses = [
    stacked
      ? "grid grid-cols-1 gap-y-1"
      : "grid grid-cols-1 md:grid-cols-[minmax(160px,240px)_1fr] gap-x-6 gap-y-1",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const labelClasses = [
    "text-sm font-medium text-foreground",
    stacked ? "" : "md:pt-2",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClasses}>
      <label
        id={labelId}
        htmlFor={effectiveControlId}
        className={labelClasses}
      >
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </label>
      <div className="min-w-0">
        {cloned}
        {help && (
          <p
            id={helpId}
            className="mt-1.5 text-xs leading-relaxed text-muted-foreground"
          >
            {help}
          </p>
        )}
        {error && (
          <p id={errorId} className="mt-1 text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
